import { AGENCY_ATTRIBUTION_KEYS } from './agencyAttributionSession';

// Google Analytics quote-funnel events (website tracking plan, Oct 2026).
//
// The app pushes events onto `window.dataLayer`; the Tag Manager container
// (REACT_APP_GTM_ID, the marketing site's GTM-KDJ3SVCM) forwards them to GA4.
// Nothing here talks to GA directly, so a missing container costs the
// analytics and nothing else — every function is a no-op that never throws.
//
// NEVER put personal information in an event: no names, emails, phone numbers,
// business names, addresses or free-text answers. It is against Google's terms.
// The parameter types below are closed on purpose — ids, coverage categories,
// amounts and fixed reason codes only.

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

export type Product = 'general_liability' | 'professional_liability';

const PRODUCT_BY_LOB: Record<string, Product> = {
  GL: 'general_liability',
  EO: 'professional_liability'
};

export type QuoteSource = 'direct' | 'broker';

export type ReferralReason =
  | 'send_to_broker'
  | 'decline_business'
  | 'application_limit'
  | 'all_products_declined';

type Currency = 'CAD' | 'USD';

interface QuoteFunnelEvents {
  quote_start: { entry_page?: string };
  quote_step: { step_number: number; step_name: string; product?: string };
  quote_price_shown: { product: string; value: number; currency: Currency };
  quote_complete: {
    quote_id: string;
    product: string;
    value: number;
    currency: Currency;
  };
  quote_referred: { referral_reason: ReferralReason; product?: string };
}

export type QuoteFunnelEvent = keyof QuoteFunnelEvents;

const FLOW_KEY = 'analyticsQuoteFlow';
const STEPS_KEY = 'analyticsQuoteSteps';
const COMPLETED_KEY = 'analyticsQuoteCompleted';
const NEW_BUSINESS = 'New Business';

const readSession = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSession = (key: string, value: string) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage blocked (private mode, quota): events still fire, minus dedupe.
  }
};

const readList = (key: string): string[] => {
  try {
    const parsed: unknown = JSON.parse(readSession(key) ?? '[]');
    return Array.isArray(parsed)
      ? parsed.filter((v): v is string => typeof v === 'string')
      : [];
  } catch {
    return [];
  }
};

/**
 * Which deployment sent the event — REACT_APP_STAGE: `production`, `staging`,
 * `development` or `local`. Stage and production share one container and one
 * GA property, so this is what tells their data apart.
 */
export const getAppEnv = (): string => process.env.REACT_APP_STAGE || 'unknown';

/**
 * Loads the Tag Manager container. The standard snippet's <noscript> half is
 * omitted: this app renders nothing without JavaScript, so there is no page
 * for it to measure.
 */
export const loadTagManager = (containerId: string | undefined): void => {
  if (!containerId || typeof document === 'undefined') return;
  if (document.getElementById('gtm-script')) return;
  window.dataLayer = window.dataLayer || [];
  // Pushed before the container loads, so every tag — page views included —
  // can read it as a Data Layer Variable.
  window.dataLayer.push({ app_env: getAppEnv() });
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.id = 'gtm-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
    containerId
  )}`;
  document.head.appendChild(script);
};

/** 'GL' / 'EO' → GA product name; several products are joined with '+'. */
export const toProduct = (lobTypes: string[]): string =>
  Array.from(
    new Set(
      lobTypes
        .map((lob) => PRODUCT_BY_LOB[lob])
        .filter((p): p is Product => !!p)
    )
  )
    .sort()
    .join('+');

export const toCurrency = (region: string | null | undefined): Currency =>
  region === 'Canada' ? 'CAD' : 'USD';

/** 'broker' when the tab carries agency/broker attribution, else 'direct'. */
export const getQuoteSource = (): QuoteSource => {
  try {
    const stored: unknown = JSON.parse(
      readSession('agencyAttribution') ?? '{}'
    );
    if (stored && typeof stored === 'object') {
      const record = stored as Record<string, unknown>;
      if (AGENCY_ATTRIBUTION_KEYS.some((key) => !!record[key])) {
        return 'broker';
      }
    }
  } catch {
    // fall through
  }
  return 'direct';
};

/**
 * The funnel measures new business only. An endorsement or renewal opened in
 * this tab marks it, and later events in the tab are dropped. A tab with no
 * mark (a resumed quote, opened cold) counts as new business.
 */
const isNewBusinessTab = (): boolean => {
  const flow = readSession(FLOW_KEY);
  return !flow || flow === NEW_BUSINESS;
};

export function trackEvent<E extends QuoteFunnelEvent>(
  event: E,
  params: QuoteFunnelEvents[E]
): void {
  try {
    if (!isNewBusinessTab()) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      app_env: getAppEnv(),
      quote_source: getQuoteSource(),
      ...params
    });
  } catch {
    // Analytics must never break the quote flow.
  }
}

/**
 * The first screen of the quote flow. Resets the tab's step list so a second
 * quote in the same tab numbers its steps from 1 again.
 */
export const trackQuoteStart = (transactionType: string): void => {
  writeSession(FLOW_KEY, transactionType);
  writeSession(STEPS_KEY, '[]');
  let entryPage: string | undefined;
  try {
    // Origin + path only: a query string can carry anything.
    if (document.referrer) {
      const url = new URL(document.referrer);
      entryPage = `${url.origin}${url.pathname}`;
    }
  } catch {
    entryPage = undefined;
  }
  trackEvent('quote_start', { entry_page: entryPage });
};

/**
 * A survey page the visitor completed. `stepName` is the SurveyJS page name,
 * which is stable across releases and holds no answers. Its number is the
 * order in which the tab first completed it, so going back and forward again
 * re-sends the same number rather than inventing a new step.
 */
export const trackQuoteStep = (stepName: string, product?: string): void => {
  const steps = readList(STEPS_KEY);
  let index = steps.indexOf(stepName);
  if (index === -1) {
    steps.push(stepName);
    writeSession(STEPS_KEY, JSON.stringify(steps));
    index = steps.length - 1;
  }
  trackEvent('quote_step', {
    step_number: index + 1,
    step_name: stepName,
    product: product || undefined
  });
};

/**
 * A finished quote, sent once per application group per tab, so a refresh or
 * a coverage change on the quote page is not a second completion.
 */
export const trackQuoteComplete = (
  applicationGroupId: string,
  params: QuoteFunnelEvents['quote_complete']
): void => {
  const completed = readList(COMPLETED_KEY);
  if (completed.includes(applicationGroupId)) return;
  writeSession(
    COMPLETED_KEY,
    JSON.stringify([...completed, applicationGroupId])
  );
  trackEvent('quote_complete', params);
};
